import type { PolymorphicProps } from "@kobalte/core";
import type { IconTypes } from "solid-icons";
import { TbLoader2 } from "solid-icons/tb";
import { Show, type ValidComponent, splitProps } from "solid-js";
import { Dynamic } from "solid-js/web";
import { type ClassNameValue, twMerge } from "tailwind-merge";

type ExtraProps = {
  icon?: IconTypes;
  iconClass?: string;
  noBorder?: boolean;
  noColor?: boolean;
  loading?: boolean;
};

export const StButtonClasses = (split: { noColor?: boolean; noBorder?: boolean }, ...classLists: ClassNameValue[]) =>
  twMerge(
    `group/btn outline-none
  flex items-center justify-center gap-1.5 text-xs rounded-lg p-1.5 cursor-pointer
  focus:ring-2 ring-brand transition-all`,
    !split.noColor && "bg-panel hover:bg-panel-hover text-primary-text disabled:text-muted-text disabled:bg-panel",
    !split.noBorder && "border border-muted-border hover:border-primary-border",
    ...classLists,
  );

export const StButton = <T extends ValidComponent = "button">(props: PolymorphicProps<T, ExtraProps>) => {
  const [split, rest] = splitProps(props, ["icon", "noBorder", "loading", "noColor", "iconClass"]);

  return (
    <Dynamic
      component={props.as ?? "button"}
      {...rest}
      disabled={split.loading}
      class={StButtonClasses(split, props.class)}
    >
      <Show when={split.icon || split.loading}>
        <Dynamic
          component={split.loading ? TbLoader2 : split.icon}
          class={twMerge(
            "group-hover/btn:scale-hover group-active/btn:scale-active transition-transform size-4 ",
            split.iconClass,
          )}
          classList={{
            "animate-spin": split.loading,
          }}
        />
      </Show>

      {rest.children}
    </Dynamic>
  );
};
